import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { gmContent } from '../content/gm';
import { useStrings } from '../locales';
import './GMTools.css';

const ALL_TABS = Object.keys(gmContent);
/* Handout sheets carry their own heading (the printed tab) */
const SHEET_TABS = new Set(['VILLAIN BUILDER', 'DOOM TRACK']);

export default function GMTools() {
  const s = useStrings().gm;
  const LABELS: Record<string, string> = {
    'PRINCIPLES':   s.tabs.principles,
    'FEAR MOVES':   s.tabs.fearMoves,
    'GM MECHANICS': s.tabs.gmMechanics,
    'ADVERSARIES':  s.tabs.adversaries,
    'VILLAIN BUILDER': s.tabs.villainBuilder,
    'DOOM TRACK':   s.tabs.doomTrack,
  };
  const label = (key: string) => LABELS[key] ?? key.charAt(0) + key.slice(1).toLowerCase();

  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<string>(() => {
    const raw = (searchParams.get('tab') ?? '').toUpperCase();
    return ALL_TABS.includes(raw) ? raw : ALL_TABS[0];
  });

  const currentIdx = ALL_TABS.indexOf(activeTab);
  const prevTab = currentIdx > 0 ? ALL_TABS[currentIdx - 1] : null;
  const nextTab = currentIdx < ALL_TABS.length - 1 ? ALL_TABS[currentIdx + 1] : null;

  const goTo = (key: string) => {
    setActiveTab(key);
    document.querySelector('.content-area')?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const Content = gmContent[activeTab];

  return (
    <div className="page-content">
      <div className="page-header">
        <h1>{s.title}</h1>
        <p>{s.desc}</p>
      </div>

      <div className="mechanics-layout">
        {/* Sidebar TOC — desktop */}
        <nav className="mechanics-toc">
          <h4>{s.contents}</h4>
          {ALL_TABS.map((key) => (
            <button
              key={key}
              className={`toc-link${activeTab === key ? ' active' : ''}`}
              onClick={() => goTo(key)}
            >
              {label(key)}
            </button>
          ))}
        </nav>

        <div className="mechanics-content">
          {/* Dropdown + counter — mobile only */}
          <div className="mechanics-mobile-nav">
            <select
              className="mechanics-select"
              value={activeTab}
              onChange={(e) => goTo(e.target.value)}
              aria-label="Select section"
            >
              {ALL_TABS.map((key) => (
                <option key={key} value={key}>{label(key)}</option>
              ))}
            </select>
            <span className="mechanics-select-count">
              {currentIdx + 1} / {ALL_TABS.length}
            </span>
          </div>

          {/* Content */}
          {Content && (
            <div className="rule-section">
              {!SHEET_TABS.has(activeTab) && <h2>{label(activeTab)}</h2>}
              <Content />
            </div>
          )}

          {/* Prev / Next — mobile only */}
          <div className="mechanics-section-nav">
            {prevTab
              ? <button className="mechanics-nav-btn mechanics-nav-btn--prev" onClick={() => goTo(prevTab)}>
                  <ChevronLeft size={20} className="mechanics-nav-btn__arrow" />
                  <span className="mechanics-nav-btn__label">{label(prevTab)}</span>
                </button>
              : <span />
            }
            {nextTab
              ? <button className="mechanics-nav-btn mechanics-nav-btn--next" onClick={() => goTo(nextTab)}>
                  <span className="mechanics-nav-btn__label">{label(nextTab)}</span>
                  <ChevronRight size={20} className="mechanics-nav-btn__arrow" />
                </button>
              : <span />
            }
          </div>
        </div>
      </div>
    </div>
  );
}
